// Client-side mirror of the server's flagRollout() derivation.
//
// Kept in sync manually — if server logic changes, update here too.
// Split from squapiClient.ts (server) so the web bundle doesn't pull in
// server-only code paths.
import type { FeatureFlag, ReleaseItem, RolloutState } from "@release-hub/shared";

export function flagRollout(flag: FeatureFlag): RolloutState {
  if (flag.globalIsDisabled) {
    return { kind: "disabled", label: "Globally disabled", workspace_count: 0, ga_via: null };
  }
  // GA is defined *only* by the wildcard "*" in the workspaces list.
  // `defaultEnabled` is a squapi implementation detail and does not count.
  const hasWildcard = flag.workspaces.includes("*");

  if (hasWildcard && flag.disallowedWorkspaces.length === 0) {
    return { kind: "ga", label: "GA", workspace_count: 0, ga_via: "wildcard" };
  }
  if (hasWildcard && flag.disallowedWorkspaces.length > 0) {
    const n = flag.disallowedWorkspaces.length;
    return {
      kind: "ea",
      label: `GA except ${n} workspace${n === 1 ? "" : "s"}`,
      workspace_count: 0,
      ga_via: null,
    };
  }
  const specific = flag.workspaces.filter((w) => w !== "*");
  if (specific.length === 0) {
    return { kind: "unreleased", label: "Unreleased", workspace_count: 0, ga_via: null };
  }
  return {
    kind: "ea",
    label: `EA · ${specific.length} workspace${specific.length === 1 ? "" : "s"}`,
    workspace_count: specific.length,
    ga_via: null,
  };
}

/** GA/EA classifier for a release_item.
 *
 * Priority:
 *   1. Manual override on the item ('ga' | 'ea') — editor forced it.
 *   2. No linked flags → GA (item shipped without gating).
 *   3. Linked flags → follow the least-advanced flag. Unknown flag ids
 *      default to EA (safer — surfaces the missing flag). */
export function derivedItemRollout(
  item: Pick<ReleaseItem, "linked_flag_ids" | "rollout_override">,
  flagsById: Map<string, FeatureFlag>,
): "ga" | "ea" {
  if (item.rollout_override === "ga" || item.rollout_override === "ea") {
    return item.rollout_override;
  }
  const linked = item.linked_flag_ids ?? [];
  if (linked.length === 0) return "ga";
  for (const fid of linked) {
    const flag = flagsById.get(fid);
    if (!flag) return "ea";
    if (flagRollout(flag).kind !== "ga") return "ea";
  }
  return "ga";
}

